import { makeAuthenticatedRequest } from '../utils/api';
import { auth } from '../config/firebase';
import { logError } from '../utils/logs';

const getVerboseLogs = () => process.env.NODE_ENV !== 'production' || globalThis.VERBOSE_LOGS;

export const createUserAsAdmin = async (firstName, lastName, email, password, role) => {
	try {
		const url = '/api/admin/createUser';
		const options = {
			method: 'POST',
			body: JSON.stringify({
				firstName,
				lastName,
				email,
				password,
				role,
			}),
		};

		const response = await makeAuthenticatedRequest(url, options);

		return response;
	} catch (error) {
		const errorMessage = error?.message || 'Unknown error occurred';
		throw new Error(`Failed to create user: ${errorMessage}`);
	}
};

export const sendUserActivationEmail = async (email, role) => {
	try {
		const currentUser = auth.currentUser;
		const verboseLogging = getVerboseLogs();

		if (verboseLogging) {
			logError('Starting sendUserActivationEmail (admin)', null, {
				hasCurrentUser: !!currentUser,
				targetEmail: email,
				role,
			});
		}

		if (!email) {
			throw new Error('Email is required');
		}
		if (!role) {
			throw new Error('Role is required');
		}

		const url = '/api/admin/sendActivationEmail';
		const options = {
			method: 'POST',
			body: JSON.stringify({
				email,
				role,
			}),
		};

		if (verboseLogging) {
			logError('About to call makeAuthenticatedRequest', null, { url, email, role });
		}

		const response = await makeAuthenticatedRequest(url, options);

		if (verboseLogging) {
			logError('Backend acknowledged activation email (admin)', null, {
				success: response?.success,
			});
		}

		if (verboseLogging) {
			logError('Admin activation email flow complete', null, {
				note: 'Admin-initiated activation relies on backend email link generation',
			});
		}

		return response;
	} catch (error) {
		logError('sendUserActivationEmail failed', error, {
			email,
			role,
			errorMessage: error?.message,
			errorName: error?.name,
		});
		const errorMessage = error?.message || 'Unknown error occurred';
		throw new Error(`Failed to send activation email: ${errorMessage}`);
	}
};

export const getRoles = async () => {
	try {
		const url = '/api/admin/getRoles';
		const options = {
			method: 'GET',
		};

		const response = await makeAuthenticatedRequest(url, options);

		return response;
	} catch (error) {
		throw new Error(`Failed to get user roles: ${error.message}`);
	}
};

export const getUsersWithRoles = async () => {
	try {
		const url = '/api/admin/getUsersWithRoles';
		const options = {
			method: 'GET',
		};

		const response = await makeAuthenticatedRequest(url, options);

		return response;
	} catch (error) {
		throw new Error(`Failed to get users with roles: ${error.message}`);
	}
};

export const deleteUser = async userId => {
	try {
		const url = '/api/admin/deleteUser';
		const options = {
			method: 'POST',
			body: JSON.stringify({
				userId,
			}),
		};

		const response = await makeAuthenticatedRequest(url, options);

		return response;
	} catch (error) {
		throw new Error(`Failed to delete user: ${error.message}`);
	}
};

export const updateUser = async ({
	userId,
	firstName,
	lastName,
	role,
	isActive,
	organizationId,
}) => {
	try {
		const url = '/api/admin/updateUser';
		const options = {
			method: 'POST',
			body: JSON.stringify({ userId, firstName, lastName, role, isActive, organizationId }),
		};

		const response = await makeAuthenticatedRequest(url, options);

		return response;
	} catch (error) {
		throw new Error(`Failed to update user: ${error.message}`);
	}
};

export const getClockifyKeysStatus = async () => {
	try {
		return await makeAuthenticatedRequest('/api/admin/clockify-keys', {
			method: 'GET',
		});
	} catch (error) {
		throw new Error(`Failed to fetch Clockify key statuses: ${error.message}`);
	}
};

export const getClockifyKeyStatusByUser = async userId => {
	try {
		return await makeAuthenticatedRequest(`/api/admin/clockify-keys/${userId}/status`, {
			method: 'GET',
		});
	} catch (error) {
		throw new Error(`Failed to fetch Clockify key status: ${error.message}`);
	}
};

export const upsertClockifyKeyForUser = async ({ userId, apiKey, validateOnSave = true }) => {
	try {
		return await makeAuthenticatedRequest(`/api/admin/clockify-keys/${userId}`, {
			method: 'PUT',
			body: JSON.stringify({ apiKey, validateOnSave }),
		});
	} catch (error) {
		throw new Error(`Failed to save Clockify API key: ${error.message}`);
	}
};

export const validateClockifyKeyForUser = async userId => {
	try {
		return await makeAuthenticatedRequest(`/api/admin/clockify-keys/${userId}/validate`, {
			method: 'POST',
		});
	} catch (error) {
		throw new Error(`Failed to validate Clockify API key: ${error.message}`);
	}
};

export const deleteClockifyKeyForUser = async userId => {
	try {
		return await makeAuthenticatedRequest(`/api/admin/clockify-keys/${userId}`, {
			method: 'DELETE',
		});
	} catch (error) {
		throw new Error(`Failed to delete Clockify API key: ${error.message}`);
	}
};
